import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  AuthProvider as DescopeAuthProvider,
  getSessionToken,
  useSession,
} from '@descope/react-sdk';
import {
  FirebaseSyncContext,
  FirebaseSyncStatus,
  useSessionClaims,
} from '@percihealth/react-auth';
import { FirebaseApp } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  signInWithCustomToken,
} from 'firebase/auth';
import { firestoreSubscriptionPermissions } from '@packages/core-shared';
import { fetchFirebaseCustomToken } from '../exchangeDescopeForFirebase';

// How long to wait before retrying a failed sign-OUT. Sign-in failures are
// recovered by the user (the sync-error screen's Try again); a sign-out has no
// such surface, so it retries itself.
const RETRY_DELAY_MS = 3000;

// Reconciles the Firebase subscription session with the Descope session so the
// two never drift apart, and reports through FirebaseSyncContext whether
// Firebase currently reflects the Descope session - so PrivateElement can hold
// protected content (and its live Firestore/Storage reads) until it does.
//
// The API and the UI already authorize on the Descope session directly
// (PPL-3826); Firebase exists only so the app can subscribe to Firestore and
// read Storage, and its custom claims carry the subscription-permission subset.
// The desired state, re-asserted on every change to EITHER session, is:
//
// - Descope not authenticated -> Firebase signed out.
// - Descope authenticated     -> Firebase signed in for the SAME account with
//   the current subscription permissions.
//
// Both auth systems settle asynchronously and independently, so: the effect
// watches Firebase's own state (`onAuthStateChanged`) as well as the Descope
// session; the desired state (`target`) is keyed on account IDENTITY as well as
// the permissions, so a switch to another account never leaves Firebase as the
// previous one; every sign-in/sign-out runs on a single serialized chain, and
// each async step checks its captured `target` against the latest desired one
// (`targetRef`) so a superseded operation cannot win (a sign-in stale on
// completion undoes itself) while our OWN sign-in's `onAuthStateChanged` echo -
// which does not change the target - is not mistaken for a supersession; the
// status is DERIVED during render from `syncedTarget`, so a target change reads
// as `syncing` on the very render it happens rather than a frame late; a failed
// exchange is `error` with a working `retry`, never silently stuck.
function FirebaseSyncGate({
  firebaseApp,
  children,
}: {
  firebaseApp: FirebaseApp;
  children: ReactNode;
}) {
  const { isSessionLoading, isAuthenticated } = useSession();
  // The session TOKEN's claims, not `useSession().claims`: the latter carries
  // neither the permissions nor the name (see `useSessionClaims`).
  const claims = useSessionClaims();
  // The email of Firebase's own current user (`null` when signed out) - the
  // identity, not just a boolean, so a Firebase user for a DIFFERENT account
  // is not mistaken for the right one.
  const [firebaseEmail, setFirebaseEmail] = useState<string | null>(null);
  const [firebaseReady, setFirebaseReady] = useState(false);
  // The target the Firebase session has been reconciled to, and the target an
  // exchange failed for. Both are state, so a change re-renders and the status
  // below recomputes. `null` target = the signed-out end-state.
  const [syncedTarget, setSyncedTarget] = useState<string | null>(null);
  const [erroredTarget, setErroredTarget] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const chain = useRef<Promise<void>>(Promise.resolve());
  const retryTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  // The target an exchange is already running for. Our own sign-in updates
  // `firebaseEmail` (via onAuthStateChanged) before the exchange records
  // `syncedTarget`, so the effect would otherwise queue a second exchange for
  // the same target; this coalesces them.
  const inflightTarget = useRef<string | null>(null);

  const settled = !isSessionLoading && firebaseReady;
  // The account the Firebase user is minted for (postDescope keys it on the
  // session email).
  const sessionEmail = typeof claims?.email === 'string' ? claims.email : '';
  // The desired Firebase state: that account and the subscription permissions
  // its claims carry - or `null` when there is no authenticated Descope
  // session. NOT the display name: the header reads that from the Descope
  // session directly, so a name-only change must not re-mint and restart the
  // Firestore/Storage listeners.
  const target =
    settled && isAuthenticated
      ? JSON.stringify({
          email: sessionEmail,
          permissions: firestoreSubscriptionPermissions(
            claims?.permissions,
          ).sort(),
        })
      : null;
  // The latest desired target, readable inside the async reconciliation to
  // tell "this is still what we want" from "a newer target superseded it" -
  // WITHOUT counting our own sign-in's onAuthStateChanged echo as a change,
  // which a render-generation counter would.
  const targetRef = useRef(target);
  targetRef.current = target;

  // In step only when Firebase's CURRENT user is the right account AND was
  // minted for the current target. Checking the actual email (not a boolean)
  // catches Firebase reporting a different user than the one last minted.
  const firebaseInStep =
    firebaseEmail !== null &&
    firebaseEmail === sessionEmail &&
    syncedTarget === target;

  // Status is DERIVED during render, not set from the effect: on the render
  // where `target` changes (account switch, permission re-mint) `syncedTarget`
  // still holds the previous one, so this flips to `syncing` immediately -
  // before PrivateElement can mount listeners for the old identity/claims.
  let status: FirebaseSyncStatus;
  if (!settled) {
    status = 'syncing';
  } else if (isAuthenticated) {
    status = firebaseInStep
      ? 'ready'
      : erroredTarget === target
        ? 'error'
        : 'syncing';
  } else {
    // Signed out: ready once Firebase is torn down too. PrivateElement anyway
    // redirects to /login on the Descope session, so this grants nothing.
    status = firebaseEmail !== null ? 'syncing' : 'ready';
  }

  const retry = useCallback(() => {
    setErroredTarget(null);
    setRetryNonce((n) => n + 1);
  }, []);

  // Track Firebase's own auth state so the reconciliation reruns when Firebase
  // restores, drops, or changes its persisted session, not only when Descope
  // changes. The email is what identifies the account.
  useEffect(() => {
    return onAuthStateChanged(getAuth(firebaseApp), (user) => {
      setFirebaseEmail(user ? (user.email ?? '') : null);
      setFirebaseReady(true);
    });
  }, [firebaseApp]);

  // Clear any pending sign-out retry on unmount so it cannot fire afterwards.
  useEffect(
    () => () => {
      if (retryTimer.current) {
        clearTimeout(retryTimer.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!settled) {
      return;
    }

    const auth = getAuth(firebaseApp);

    // A retry scheduled while still signed out; drop it if the desired state
    // has since moved on.
    const scheduleRetry = () => {
      if (retryTimer.current) {
        clearTimeout(retryTimer.current);
      }
      retryTimer.current = setTimeout(() => {
        if (targetRef.current === null) {
          setRetryNonce((n) => n + 1);
        }
      }, RETRY_DELAY_MS);
    };

    // Descope signed out: sign Firebase out too.
    if (!isAuthenticated) {
      if (!auth.currentUser) {
        return;
      }
      chain.current = chain.current
        .then(async () => {
          if (targetRef.current === null && auth.currentUser) {
            await auth.signOut();
          }
        })
        .catch(() => {
          // The Descope session is gone but Firebase did not sign out. Try
          // again shortly so the stale credential and its live listeners do
          // not linger; onAuthStateChanged would not fire to re-run this on
          // its own.
          if (targetRef.current === null) {
            scheduleRetry();
          }
        });
      return;
    }

    // Already in step, or an exchange for this target is already running:
    // nothing to do.
    if (firebaseInStep || inflightTarget.current === target) {
      return;
    }

    inflightTarget.current = target;
    chain.current = chain.current
      .then(async () => {
        // A newer target superseded this before it started.
        if (targetRef.current !== target) {
          return;
        }
        const token = getSessionToken();
        if (!token) {
          throw new Error('no Descope session token');
        }
        const customToken = await fetchFirebaseCustomToken(token);
        // Superseded during the fetch (permission change, account switch, or a
        // sign-out): drop this result rather than install stale claims or
        // revive a signed-out session.
        if (targetRef.current !== target) {
          return;
        }
        await signInWithCustomToken(auth, customToken);
        // The sign-in echoes through onAuthStateChanged, which does NOT change
        // the desired target - so decide on the target, not on whether a render
        // happened. Still the desired target: mark it synced. A genuinely newer
        // target: undo this now-stale sign-in and let the next run set the
        // right one.
        if (targetRef.current === target) {
          setSyncedTarget(target);
        } else if (auth.currentUser) {
          await auth.signOut();
        }
      })
      .catch(() => {
        // Fail closed and offer a retry (via `retry`), rather than leaving the
        // UI stuck: nothing else re-runs the effect on its own.
        if (targetRef.current === target) {
          setErroredTarget(target);
        }
      })
      .finally(() => {
        if (inflightTarget.current === target) {
          inflightTarget.current = null;
        }
      });
  }, [
    settled,
    isAuthenticated,
    target,
    firebaseInStep,
    firebaseEmail,
    syncedTarget,
    firebaseApp,
    retryNonce,
  ]);

  return (
    <FirebaseSyncContext.Provider value={{ status, retry }}>
      {children}
    </FirebaseSyncContext.Provider>
  );
}

/**
 * Makes the Descope session available to the whole app.
 *
 * Since PPL-3826 the Descope session token is EBP's API credential and the
 * source of the permissions the UI gates on, so the SDK has to wrap every
 * page, not just the sign-in form: `ApiRepository` reads the session token to
 * authorize its calls, and `usePermissions` reads the session claims. Firebase
 * is kept only for Firestore/Storage subscriptions, kept in step (and its
 * readiness reported) by `FirebaseSyncGate`.
 *
 * - `persistTokens` keeps the session in web storage, so a reload restores the
 *   API credential and the permissions instead of bouncing the user to sign in
 *   again.
 * - `autoRefresh` renews the session in the background so a long working
 *   session does not expire mid-use.
 *
 * The tokens stay in web storage rather than cookies on purpose: the API is a
 * different origin and reads the session token from the `Authorization`
 * header, not from a cookie.
 */
export default function DescopeSessionProvider({
  firebaseApp,
  children,
}: {
  firebaseApp: FirebaseApp;
  children: ReactNode;
}) {
  return (
    <DescopeAuthProvider
      projectId={process.env.REACT_APP_DESCOPE_PROJECT_ID!}
      persistTokens
      autoRefresh
    >
      <FirebaseSyncGate firebaseApp={firebaseApp}>{children}</FirebaseSyncGate>
    </DescopeAuthProvider>
  );
}
